import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useTenant } from '@/contexts/TenantContext';
import { generateAdDivId } from '@/config/ads.config';
import AdSlot from '@/components/ads/AdSlot';

// In development we render ad placeholders even when GAM is disabled for the tenant,
// so layout/positioning can be verified without live ad serving.
const showAdPlaceholders = import.meta.env.MODE === 'development';

interface PicksUpdatedModalProps {
  isOpen: boolean;
  /**
   * Contest-uploaded artwork for this popup, from `style_config.entry.post_entry_image`
   * (336x280). When set it REPLACES the GAM `POST_ENTRY` slot rather than stacking with it —
   * two medium rectangles in one popup is not a layout anyone wants. Also the default for
   * phones when no mobile asset is uploaded.
   */
  postEntryImageUrl?: string;
  /**
   * The narrower phone asset, `style_config.entry.post_entry_image_mobile` (279x170).
   * Optional — without it the desktop asset is shown on phones too.
   */
  postEntryImageUrlMobile?: string;
  /**
   * The single way out, shared by the CTA, the X, Escape and the overlay. Entry is
   * finished, so there is nothing left to do on the contest behind this popup — every
   * control acknowledges the confirmation and takes the player onward, to the lobby app
   * for tenants that have one and to My Picks otherwise. The caller owns that choice;
   * this modal only labels it.
   */
  onExit: () => void;
  /** CTA copy, so the label matches wherever `onExit` actually goes. */
  exitLabel: string;
}

const PicksUpdatedModal: React.FC<PicksUpdatedModalProps> = ({
  isOpen,
  onExit,
  exitLabel,
  postEntryImageUrl,
  postEntryImageUrlMobile,
}) => {
  const { t } = useTranslation();
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();

  /**
   * Has this contest supplied its own artwork for the slot? Trimmed because an image field
   * cleared in admin can persist as whitespace in `style_config`, which is not a URL and must
   * not count as one.
   */
  const uploadedDesktop = postEntryImageUrl?.trim() || undefined;
  const uploadedMobile = postEntryImageUrlMobile?.trim() || undefined;
  const hasUploadedImage = Boolean(uploadedDesktop || uploadedMobile);

  // Resolve the concrete placement first: isPlacementEnabled is derived from the
  // raw placements list, but TenantContext drops placements with no adUnitPath, so
  // a placement can be "enabled" yet absent from adPlacements. Guard on the data
  // AdSlot/generateAdDivId actually dereference (adUnitPath + divIdPrefix).
  const postEntryPlacement = adPlacements?.POST_ENTRY;

  // An uploaded asset wins outright: the contest has said what belongs in this space, so the
  // ad is not merely hidden behind it but never requested.
  const showPostEntryAd =
    !hasUploadedImage &&
    !!postEntryPlacement &&
    !!postEntryPlacement.adUnitPath &&
    !!postEntryPlacement.divIdPrefix &&
    isPlacementEnabled('POST_ENTRY') &&
    (isAdsEnabled || showAdPlaceholders);

  return (
    <Dialog open={isOpen} onOpenChange={onExit}>
      <DialogContent
        data-test-id="picks-updated-modal"
        className="bg-[#1a1a1a] border-0 w-[calc(100%-2rem)] max-w-md rounded-2xl p-0 overflow-hidden [&>button:last-child]:hidden"
      >
        {/* Close button */}
        <button
          data-test-id="close-modal-btn"
          onClick={onExit}
          className="absolute right-4 top-4 text-gray-400 hover:text-white transition-colors z-50"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Content */}
        <div className="relative">
          <div className="pt-8 px-8 pb-8">
            {/* Title */}
            <h2 className="text-white text-3xl font-bold text-center mb-4">
              {t('modal.picksSubmitted.title')}
            </h2>

            {/* Subtitle */}
            <p className="text-gray-300 text-center text-lg mb-8">
              {t('modal.picksSubmitted.message')} <br />
              {t('modal.picksSubmitted.editMessage')}
            </p>

            {/* Contest-uploaded artwork, which takes this space when there is any.

                Chosen by `<picture>` rather than a JS breakpoint hook: the browser resolves
                the media query before it fetches, so exactly one file is downloaded and there
                is no flash of the wrong asset as the popup opens. The `<img>` carries the
                MOBILE asset so the fallback resolves through `?? uploadedDesktop` — a contest
                with only the desktop asset shows it everywhere. Drawn at its own ratio, so
                nothing is cropped. */}
            {hasUploadedImage && (
              <div className="mb-6 flex justify-center">
                <picture>
                  {uploadedDesktop && uploadedMobile && (
                    <source media="(min-width: 768px)" srcSet={uploadedDesktop} />
                  )}
                  <img
                    src={uploadedMobile ?? uploadedDesktop}
                    alt=""
                    data-test-id="post-entry-image"
                    // `h-auto`: the two assets have different ratios (336x280 vs 279x170), so
                    // the height follows whichever one the browser picked.
                    className="block h-auto w-full max-w-[279px] md:max-w-[336px]"
                  />
                </picture>
              </div>
            )}

            {/* Post-entry ad slot (placement key: POST_ENTRY) — only when no artwork was
                uploaded for this contest. */}
            {showPostEntryAd && adPlacements && (
              <div className="mb-6 flex justify-center">
                <AdSlot
                  divId={generateAdDivId(adPlacements, 'POST_ENTRY', false)}
                  adPlacement={postEntryPlacement}
                  placement="post-entry"
                />
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">
              <Button
                data-test-id="return-to-lobby-btn"
                onClick={onExit}
                variant="default"
                className="w-full bg-pickem-primary hover:bg-pickem-primary/90 text-white font-semibold py-6 text-lg rounded-xl"
              >
                {exitLabel}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PicksUpdatedModal;
